import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { MenuSupplement } from '../models/supplement.model';

export interface CategoryResponse {
  id?: number;
  name?: string;
  parent?: number;
  description?: string;
  display?: string;
  slug?: string;
  image?: {
    id?: number;
    src?: string;
    alt?: string;
  };
  count?: number;
  menu_order?: number;
}

export interface ProductResponse {   
  id: number;
  name: string;
  slug: string;
  price: string;
  regular_price: string;
  sale_price: string;
  status: string;
  description: string;
  short_description: string;
  on_sale: boolean;
  images: {
    id: number;
    src: string;
    alt: string;
  }[];
  categories: {
    id: number;
    name: string;
    slug: string;
  }[];
  meta_data: any;
  available_in_the_menu?: boolean;
  /**
   * Menu add-ons offered on this product. The `api/v4/shops/{id}/menu` payload has carried these
   * all along (iOS decodes them from this very endpoint) — they were simply untyped here.
   *
   * Absent for WooCommerce venues and for any venue with add-ons switched off, so absent and empty
   * both mean "this product has none".
   */
  supplements?: MenuSupplement[];
}

/**
 * What /payment-status answers with.
 *
 * ⚠️ `rc_code` is a STRING and must stay one — RC is alphanumeric, and a
 * soft-declined VISA answers '1A'. Nothing here may parse it as a number.
 */
export interface PaymentStatusResponse {
  status: 'pending' | 'success' | 'declined' | 'error' | 'reversed' | 'expired';
  action_code?: string | null;
  rc_code?: string | null;
  message?: string | null;
  card?: string | null;
  card_brand?: string | null;
  /**
   * Spec Table 2: "ATTENTION! E-merchant must display the value to the cardholder."
   * An obligation, not a nicety — when present it has to reach the screen.
   */
  cardholder_info?: string | null;
}

/**
 * What /promo-codes/validate answers with.
 *
 * ⚠️ These are NUMBERS, unlike the identically-named fields on an ORDER response, which
 * are STRINGS. The two must not share a decoder — see the note on
 * `CartService.applyPromoCode()`.
 *
 * The endpoint is a PREVIEW and nothing else: it writes nothing, claims no redemption and
 * does not decide what the order costs. The server re-validates and re-prices when the
 * order is submitted, so nothing here may be treated as final.
 *
 * A **404 means the feature is switched off** for this host. That is not an error to show
 * the guest — it means the promo UI should not exist at all.
 */
export interface PromoCodeValidationResponse {
  valid: boolean;
  /** Present only when `valid` — echoed back normalised (upper-cased). */
  code?: string;
  percent?: number;
  gross_total?: number;
  discount_total?: number;
  net_total?: number;
  /** Present only when refused: `promo_code_expired`, `promo_code_not_found`, … */
  reason?: string;
  /** Present ONLY on `promo_code_minimum_not_met`. */
  minimum_order_amount?: number;
}

export interface CategoryWithProducts extends CategoryResponse {
  products?: ProductResponse[];
  subcategories?: CategoryWithProducts[];
  available_from?: string;
  available_to?: string;
}

export interface MenuLayoutInfo {
  id?: number;
  slug?: string;
  name?: string;
}

export interface FullMenuResponse {
  featured_products?: ProductResponse[];
  categories?: CategoryWithProducts[];
}

/**
 * The answer from POST /orders/{id}/pay.
 *
 * ⚠️ `transaction_created_at` is the server's clock, and the client must use it rather
 * than its own. The backend resumes an existing pending transaction for 15 minutes
 * measured from that timestamp; a resume returns the ORIGINAL row, so a client that
 * restarts its own timer on every call drifts ahead of the server and can request a
 * "resume" the server treats as a new sale — two live transactions, two payable forms,
 * one order. `resumed` says which of the two just happened.
 */
export interface PaymentInitiateResponse {
  payment_url: string;
  transaction_id?: number;
  transaction_created_at?: string;
  resumed?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private baseUrl = environment.baseUrl;

  constructor(private http: HttpClient) { }

  private getHeaders(): HttpHeaders {
    const token = sessionStorage.getItem('authToken');
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'X-Client': 'feather-app'
    });
  }

  get<T>(endpoint: string): Observable<T> {
    const headers = this.getHeaders();
    return this.http.get<T>(`${this.baseUrl}${endpoint}`, { headers });
  }

  getWithParams<T>(endpoint: string, params: HttpParams): Observable<T> {
    const headers = this.getHeaders();
    return this.http.get<T>(`${this.baseUrl}${endpoint}`, { headers, params });
  }

  post<T>(endpoint: string, data: any): Observable<T> {
    const headers = this.getHeaders();
    return this.http.post<T>(`${this.baseUrl}${endpoint}`, data, { headers });
  }

  put<T>(endpoint: string, data: any): Observable<T> {
    const headers = this.getHeaders();
    return this.http.put<T>(`${this.baseUrl}${endpoint}`, data, { headers });
  }

  // Fetch entire menu (categories with embedded products + featured products) in a single call
  getFullMenu(shopId: number): Observable<FullMenuResponse> {
    const endpoint = `api/v4/shops/${shopId}/menu`;
    return this.get<FullMenuResponse>(endpoint);
  }

  // Create a new order
  createOrder(shopId: number): Observable<any> {
    const endpoint = `api/v3/shops/${shopId}/orders`;
    const initialOrderData = {
      set_paid: false,
      line_items: []
    };
    return this.post<any>(endpoint, initialOrderData);
  }

  // Update an existing order
  updateOrder(shopId: number, orderId: number, orderData: any): Observable<any> {
    const endpoint = `api/v3/shops/${shopId}/orders/${orderId}`;
    return this.put<any>(endpoint, orderData);
  }

  // Get an existing order
  getOrder(shopId: number, orderId: number): Observable<any> {
    const endpoint = `api/v3/shops/${shopId}/orders/${orderId}`;
    const params = new HttpParams().set('expand', 'line_items,shipping');
    return this.getWithParams<any>(endpoint, params);
  }

  /**
   * Preview what a promo code is worth, before the guest commits to it.
   *
   * `subtotal` is the guest's own gross cart total. The server treats it as untrusted and
   * re-derives everything from its own figures when the order is priced, so a wrong value
   * here buys a wrong preview and nothing more.
   *
   * Throttled server-side at 20/min per shop+IP — it is a code oracle — so callers debounce
   * rather than validating on every keystroke.
   */
  validatePromoCode(shopId: number, code: string, subtotal: number): Observable<PromoCodeValidationResponse> {
    const endpoint = `api/v3/shops/${shopId}/promo-codes/validate`;
    return this.post<PromoCodeValidationResponse>(endpoint, { code, subtotal });
  }

  // Finish (submit) an order
  finishOrder(shopId: number, orderId: number, orderData: any): Observable<any> {
    const endpoint = `api/v3/shops/${shopId}/orders/${orderId}`;
    return this.put<any>(endpoint, orderData);
  }

  /**
   * Initiate a BORICA online payment and get back the URL to send the guest to.
   *
   * `language` is not optional in practice: BORICA's hosted page defaults to
   * Bulgarian whenever LANG is absent, which is the wrong language for every
   * tourist in the country the feature exists for. The gateway itself only knows
   * BG and EN — the backend narrows whatever it is given down to those two.
   */
  initiatePayment(
    shopId: number,
    orderId: number,
    language?: string,
    customerPhone?: string
  ): Observable<PaymentInitiateResponse> {
    const endpoint = `api/v3/shops/${shopId}/orders/${orderId}/pay`;

    /*
     * `client_platform` tells the backend where to send the guest once BORICA is done
     * with them. It has exactly one reader — buildReturnUrl() — and 'web' is what it
     * assumes for anyone who says nothing, so this changes no behaviour here at all.
     *
     * Sent anyway, because the column it lands in is how the NATIVE apps stop being
     * redirected into this application after paying: until they could say "ios", every
     * guest got the web menu, including the ones already standing in an app. Leaving web
     * silent would make a row's platform mean "web, or a client too old to say" forever.
     */
    const body: Record<string, string> = { client_platform: 'web' };

    if (language) {
      body['language'] = language;
    }

    /*
     * M_INFO must carry an e-mail or a telephone (spec Table 1) or the issuer is
     * entitled to challenge or decline — so a missing one shows up as a worse
     * approval rate rather than as an error.
     *
     * The backend already falls back to the order's own billing.phone, which is
     * enough for DELIVERY. It is not enough for PICKUP: that flow deliberately
     * sends no billing block at all, because an empty shipping.address_1 was
     * rejected with a 400 (see OrderPayloadService.addOffSiteMetadata). The phone
     * the guest typed then survives only in meta_data, which resolveOrder() does
     * not read.
     *
     * Passing it here rather than adding a billing block to the order keeps that
     * 400 fixed and leaves order creation alone. `customer_phone` is the exact key
     * BoricaPaymentController::initiate() prefers over the order's own value.
     */
    if (customerPhone) {
      body['customer_phone'] = customerPhone;
    }

    return this.post<PaymentInitiateResponse>(endpoint, body);
  }

  /**
   * The authoritative answer about a payment.
   *
   * The `payment=` query param the guest comes back with is user-controlled, so
   * nothing may be shown on the strength of it alone. This endpoint triggers a live
   * TRTYPE=90 status check when the stored row is still pending and older than 20
   * seconds, so it answers with what BORICA actually says rather than with a stale
   * row.
   */
  getPaymentStatus(shopId: number, orderId: number): Observable<PaymentStatusResponse> {
    const endpoint = `api/v3/shops/${shopId}/orders/${orderId}/payment-status`;
    return this.get<PaymentStatusResponse>(endpoint);
  }

  // Check if ordering is active (returns is_open_now for working hours; matches iOS OrderingActiveResponse)
  checkIsOrderingActive(shopId: number): Observable<{ is_ordering_active: boolean; is_open_now?: boolean }> {
    const endpoint = 'api/v3/shop/check-is-ordering-active';
    const params = new HttpParams().set('shop_id', shopId.toString());
    return this.getWithParams<{ is_ordering_active: boolean; is_open_now?: boolean }>(endpoint, params);
  }

  // Check if call actions are active
  checkAreCallActionsActive(shopId: number): Observable<{ are_call_actions_active: boolean }> {
    const endpoint = 'api/v3/shop/check-are-call-actions-active';
    const params = new HttpParams().set('shop_id', shopId.toString());
    return this.getWithParams<{ are_call_actions_active: boolean }>(endpoint, params);
  }

  // Get client data (shop data)
  getClientData(shopId: number): Observable<any> {
    const endpoint = `api/v3/shop/get-for-app/${shopId}`;
    return this.get<any>(endpoint);
  }

  // Get order location data
  getOrderLocation(locationId: string): Observable<any> {
    const endpoint = `api/v3/order-locations/get/${locationId}`;
    return this.get<any>(endpoint);
  }

  // Get order info (lightweight endpoint with only necessary fields)
  getOrderInfo(shopId: number): Observable<{
    has_in_store_delivery: boolean;
    has_home_delivery: boolean;
    is_order_supported: boolean;
    is_ordering_active: boolean;
    is_open_now?: boolean;
  }> {
    const endpoint = 'api/v3/shop/order-info';
    const params = new HttpParams().set('shop_id', shopId.toString());
    return this.getWithParams<{
      has_in_store_delivery: boolean;
      has_home_delivery: boolean;
      is_order_supported: boolean;
      is_ordering_active: boolean;
      is_open_now?: boolean;
    }>(endpoint, params);
  }
}
